/* Copyright (c) 2017 Backpack Health LLC */
/* global IS_ANDROID_BUILD */

import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';

import AppUtils from '../../../../utilities/AppUtils';

import { BodyMain, Small } from '../../../components/Text';
import ShareCardSummaryGroup from './ShareCardSummaryGroup';
import { ExternalLink, TextLink, CopyLink } from '../../../components/TextLink';
import FlexRow from '../../../components/FlexRow';
import { ShareCardColorModel } from '../../../appAuthorized/dataTransformComponents/ColorShareCard';
import { mobileShareUrl } from '../../../../utilities/MobileUtil';
import ProfileModel from '../../../../models/ProfileModel';
import ConnectedNestedIntlProvider from '../../../localization/ConnectedNestedIntlProvider';
import { withLanguageCode } from '../../../../utilities/localizedUtil';
import { TrackEvent } from '../../../../utilities/UserMetrics';

interface Props {
  intl?: any;
  profile: ProfileModel;
  shareCard: any;
  languageCode: string;
  shareCardColorModel: ShareCardColorModel;
  onEnableUrl(): void;
}

const ShareCardUrl = (props) => {
  const { shareCard, profile, shareCardColorModel, onShareUrl } = props;
  const shareCardColor = shareCardColorModel.cssColor(1);

  function trackMetric() {
    TrackEvent.shareCards.sharedUrl(shareCard.definitionUid, profile.isDependent());
  }

  if (IS_ANDROID_BUILD) {
    return (
      <BodyMain>
        <FormattedMessage id="shareCard.url.full.url" values={{ string1: AppUtils.getShareUrlSubdomainPrefix(), string2: shareCard.data.uid }}>
          {
            (url) => <TextLink style={{ color: shareCardColor }} onClick={() => { trackMetric(); onShareUrl(); }}>{url}</TextLink>
          }
        </FormattedMessage>
      </BodyMain >
    );
  } else {
    return (
      <BodyMain>
        <FormattedMessage id="shareCard.url.full.url" values={{ string1: AppUtils.getShareUrlSubdomainPrefix(), string2: shareCard.data.uid }}>
          {
            (url) => (
              <ExternalLink style={{ color: shareCardColor }} href={url}>
                <FormattedMessage id="shareCard.url.display.text" values={{ string1: AppUtils.getShareUrlSubdomainPrefix(), string2: shareCard.data.uid }} />
              </ExternalLink>
            )
          }
        </FormattedMessage>
      </BodyMain >
    );
  }
}

@withLanguageCode
class ShareCardSummaryPublicUrl extends React.Component<Props, {}> {

  constructor(props) {
    super(props);
    this.shareUrl = this.shareUrl.bind(this);
  }

  shareUrl() {
    const url = this.props.intl.formatMessage({ id: 'shareCard.shareURL.message.text' }, {
      string1: AppUtils.getShareUrlSubdomainPrefix(),
      string2: this.props.shareCard.data.uid,
      string3: this.props.shareCard.passcode });
    mobileShareUrl(url);
  }

  render() {
    const { shareCard, languageCode, shareCardColorModel, onEnableUrl } = this.props;

    if (shareCard.shareCardURL.isExcluded()) { return null; }
    const shareCardColor = shareCardColorModel.cssColor(1);
    const accessCode = shareCard.data.access_code;
    const uid = shareCard.data.uid;

    return (
      <ShareCardSummaryGroup>
        <div>
          <FlexRow className="font-footnote" style={{ justifyContent: 'space-between' }}>
            <FormattedMessage id="shareCard.url.field.label" />

            {
              IS_ANDROID_BUILD ? <span /> : (
                <ConnectedNestedIntlProvider locale={{ language_code: languageCode }}>
                  <FormattedMessage id="shareCard.shareURL.message.text" values={{ string1: AppUtils.getShareUrlSubdomainPrefix(), string2: uid, string3: accessCode }}>
                    {
                      (copyText) => (
                        <CopyLink copyText={copyText} style={{ color: shareCardColor }} copiedTextId="shareCard.shareURL.copied.message">
                          <FormattedMessage id="shareCard.shareURL.button.label" />
                        </CopyLink>
                      )
                    }
                  </FormattedMessage>
                </ConnectedNestedIntlProvider>
              )
            }
          </FlexRow>

          {
            shareCard.shareCardURL.isIncluded() ? (
              <div>
                <ShareCardUrl {...this.props} onShareUrl={this.shareUrl} />
                <div>
                  <ConnectedNestedIntlProvider locale={{ language_code: languageCode }}>
                    <Small>
                      <FormattedMessage id="shareCard.accessCode.display.text" values={{ string1: accessCode }} />
                    </Small>
                  </ConnectedNestedIntlProvider>
                </div>
              </div>
            ) : (
              <ConnectedNestedIntlProvider locale={{ language_code: languageCode }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div className="font-body font-color-gsMiddle">
                    <FormattedMessage id="shareCard.urlDisabled.message.text" />
                  </div>
                  <TextLink style={{ color: shareCardColor, paddingLeft: 5 }} onClick={onEnableUrl}>
                      <FormattedMessage id="shareCardView.urlEnable.button.label" />
                  </TextLink>
                </div>
              </ConnectedNestedIntlProvider>
            )
          }
        </div>
      </ShareCardSummaryGroup>
    );
  };
}

export default injectIntl(ShareCardSummaryPublicUrl);
